/**
 * Wraps a hub/Drop slot renderer so every section it returns reports when it is
 * actually seen.
 *
 * Applied ONCE around the whole renderer rather than per branch. `renderDropHubSlot`
 * has a dozen return points — nav, stage, feature, content blocks, streaming rosters,
 * news, badges — and instrumenting each would need every future branch to remember
 * to do the same. That is the pattern that already failed here: the arrival wrapper
 * exists because `Sentry.wrapCreateBrowserRouter` sat on three of five router sites
 * and nobody noticed the other two. One wrapper, no opt-in.
 *
 * The route pattern comes from `currentRoute()` — the value the arrival wrapper
 * itself computed and published — so a section row and its page-arrival row carry an
 * identical `route` and can be joined. It is read from there rather than recomputed
 * because React Router's public `useMatches()` returns `UIMatch` objects, which carry
 * no route pattern; feeding those to `patternOf` yields "/" for every page.
 */

import React from "react";
import type {SurfaceSlot} from "@/surfaces/slots";
import {currentRoute} from "@/utils/withRouteTracking";
import {useTrackSectionView} from "@/hooks/useTrackSectionView";

/**
 * Wraps one slot's rendered output in a element that observes visibility.
 *
 * A component, not a hook call inside the render function: the renderer is a plain
 * function called in a loop, so it cannot hold hook state per slot. This can.
 */
function TrackedSlot({slotId, children}: { slotId: string; children: React.ReactNode }): React.ReactElement {
    const ref = useTrackSectionView(slotId, currentRoute());

    /*
     * `display: contents` so the wrapper contributes no box — these slots sit in grid
     * and flex containers whose layout would otherwise shift by one level.
     *
     * Which is exactly why the observer CANNOT watch this element. A `display: contents`
     * box is not generated at all, so its border box is 0x0 — measured in Chrome on the
     * live site: this wrapper reports 0x0 while the same wrapper without the property
     * reports 400x300. IntersectionObserver takes its ratio as intersected area over
     * target area, so a zero-area target is permanently 0 and can never reach the 0.3
     * threshold. Shipped in 1.2.24238 and recorded not one section view against 169
     * hub and Drop arrivals.
     *
     * `useTrackSectionView` therefore steps to the first child, which does have a box.
     * The wrapper stays because the layout neutrality is still wanted.
     */
    return <div ref={ref} style={{display: "contents"}}>{children}</div>;
}

/**
 * The PAGE archetype's equivalent, for `DropPageSurface`.
 *
 * That surface does not use a slot factory at all — it maps `enabledSections` into
 * `<section className="drop-page__beat">` elements directly — so `withSectionTracking`
 * cannot reach it and the audit, which keys on the factory, could not see the gap.
 * It went out untracked in 1.2.24238, and it is the archetype most of the traffic
 * lands on: `/drop-in/:slug` took 152 of the 169 hub-and-Drop arrivals in the first
 * hours after release.
 *
 * No wrapper needed here — the `<section>` is a real box, so the observer attaches to
 * the element the page already renders.
 */
export function TrackedSection({sectionId, className, children}: { sectionId: string; className?: string; children: React.ReactNode }): React.ReactElement {
    const ref = useTrackSectionView(sectionId, currentRoute());
    return <section ref={ref} className={className}>{children}</section>;
}

type SlotRenderer = (slot: SurfaceSlot) => React.ReactNode;

export function withSectionTracking(render: SlotRenderer): SlotRenderer {
    return (slot: SurfaceSlot): React.ReactNode => {
        const node = render(slot);
        /* A slot that renders nothing was never seen — do not report it. */
        if (node === null || node === undefined || node === false) return node;
        return <TrackedSlot key={slot.id} slotId={slot.id}>{node}</TrackedSlot>;
    };
}
